/** Streaming transport for the Recovry AI widget. */
import axios from 'axios';

import { BASE_URL, V1, V2 } from './axios';
import { getAccessToken, getRefreshToken, removeTokens, setAccessToken } from '@/utils/storage';
import { getDevice, resetDevice, type ChatDevice } from '@/lib/anonId';
import { createSseParser, type SseEvent } from '@/lib/sse';

const ENDPOINT = `${BASE_URL}${V2}chat/stream/`;

export interface ChatStreamRequest {
  message: string;
  sessionId?: string | null;
  context?: Record<string, unknown> | null;
}

export interface StreamHandlers {
  onEvent: (e: SseEvent) => void;
  onPreStreamError: (status: number, body: Record<string, unknown>) => void;
}

// Module-level single-flight, mirroring the pattern in axios.ts so a REST call
// and the stream cannot refresh concurrently and invalidate each other.
let refreshing: Promise<string | null> | null = null;

async function refreshOnce(): Promise<string | null> {
  if (!refreshing) {
    refreshing = (async () => {
      const refresh = getRefreshToken();
      if (!refresh) return null;
      try {
        const { data } = await axios.post(`${BASE_URL}${V1}auth/token/refresh/`, { refresh });
        const token = data?.access ?? data?.token ?? data?.access_token ?? null;
        if (token) setAccessToken(token);
        return token;
      } catch {
        return null;
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

function buildInit(
  body: ChatStreamRequest,
  device: ChatDevice,
  token: string | null,
  signal: AbortSignal,
): RequestInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return {
    method: 'POST',
    headers,
    signal,
    cache: 'no-store',
    body: JSON.stringify({
      message: body.message,
      session_id: body.sessionId ?? null,
      device_id: device.id,
      device_token: device.token,
      client: 'web',
      context: body.context ?? null,
    }),
  };
}

export async function streamChat(
  body: ChatStreamRequest,
  handlers: StreamHandlers,
  signal: AbortSignal,
): Promise<void> {
  let token = getAccessToken();
  let device = await getDevice();
  let res = await fetch(ENDPOINT, buildInit(body, device, token, signal));

  if (res.status === 401) {
    // Two different 401s share this status: a rejected device registration
    // (re-provision once) and an expired access token (refresh once).
    const peek = (await res.clone().json().catch(() => ({}))) as { code?: string };
    if (peek.code === 'invalid_device') {
      resetDevice();
      device = await getDevice();
      res = await fetch(ENDPOINT, buildInit(body, device, token, signal));
    } else if (token) {
      token = await refreshOnce();
      if (token) {
        res = await fetch(ENDPOINT, buildInit(body, device, token, signal));
      } else {
        // Refresh failed too -- the whole session is dead (both tokens expired).
        removeTokens();
        res = await fetch(ENDPOINT, buildInit(body, device, null, signal));
      }
    }
  }

  if (!res.ok) {
    let parsed: Record<string, unknown> = {};
    try {
      parsed = await res.json();
    } catch {
      parsed = { error: 'Something went wrong. Please try again.' };
    }
    handlers.onPreStreamError(res.status, parsed);
    return;
  }

  if (!res.body) {
    handlers.onPreStreamError(0, { code: 'no_stream', error: 'Streaming unavailable.' });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  const { feed, flush } = createSseParser(handlers.onEvent);

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) {
        // The last frame may have no trailing blank line if the connection
        // closed right after it; hand it over rather than losing it.
        flush();
        break;
      }
      feed(decoder.decode(value, { stream: true }));
    }
  } finally {
    try {
      await reader.cancel();
    } catch {
      /* already closed */
    }
  }
}
